import React, { useEffect, useState } from "react";
import {
  Backdrop,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  TextField,
  ListSubheader,
  InputAdornment,
  IconButton,
  Box,
  Typography,
  InputLabel,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useSnackbar } from "notistack";
import ClearIcon from "@mui/icons-material/Clear";
import Union2 from "../../../assets/images/Union2.png";
import { useNavigate, useOutletContext } from "react-router-dom";
import _ from "lodash";
import TLinkApi from "services/tlink.api";
import MDButton from "components/MDButton";

const GenericReports = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [reportList, setReportList] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [_dropdownOptions, setDropdownOptions] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({});
  const [filteredOptions, setFilteredOptions] = useState({});
  const [searchQuery, setSearchQuery] = useState({});
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [isReportSelected, setIsReportSelected] = useState(false);
  const [debouncedSearches, setDebouncedSearches] = useState({});

  const { currentUser } = useOutletContext();
  console.log("🚀 ~ GenericReports ~ currentUser:", currentUser);
  // const selectStyle = {
  //     height: "36.5px",
  //     "& .MuiOutlinedInput-notchedOutline": {
  //         border: "1px solid #D9D9D9",
  //         borderRadius: "0px",
  //     },
  //     "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
  //         border: "1px solid #D9D9D9",
  //         borderRadius: "0px",
  //     },
  //     "& :hover .MuiOutlinedInput-notchedOutline": {
  //         border: "1px solid #D9D9D9",
  //         borderRadius: "0px",
  //     },
  // };
  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    watch,
  } = useForm({
    defaultValues: {
      report: null,
    },
  });

  const startDate = watch("start_date");
  const endDate = watch("end_date");
  const tenant_ids = watch("tenant_ids");
  const client_ids = watch("client_ids");
  const project_ids = watch("project_ids");
  const survey_ids = watch("survey_ids");
  const report = watch("report");

  const getReportList = async () => {
    try {
      setIsLoading(true);
      const data = await TLinkApi.get(`/reports/report-type-list`);
      setReportList(data.data);
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.result?.userMessage, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDropdownClientData = async (paramKey, search = "") => {

    let payload = {
      parameter_type: paramKey,
      filter: {
      },
    };
    try {
      setIsLoading(true);
      let response = await TLinkApi.post(
        `/reports/generic-list?search=${search}`,
        payload
      );
      setDropdownOptions((prev) => ({
        ...prev,
        [paramKey]: response.data,
      }));
      setFilteredOptions((prev) => ({
        ...prev,
        [paramKey]: response.data, // Set initial filtered options to all options
      }));
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.result?.userMessage, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDropdownProjectData = async (paramKey, search = "") => {
    let payload = {
      parameter_type: paramKey,
      filter: {
        client_ids: client_ids,
      },
    };
    try {
      setIsLoading(true);
      let response = await TLinkApi.post(
        `/reports/generic-list?search=${search}`,
        payload
      );
      setDropdownOptions((prev) => ({
        ...prev,
        [paramKey]: response.data,
      }));
      setFilteredOptions((prev) => ({
        ...prev,
        [paramKey]: response.data, // Set initial filtered options to all options
      }));
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.result?.userMessage, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDropdownSurveyData = async (paramKey, search = "") => {

    let payload = {
      parameter_type: paramKey,
      filter: {
        client_ids: client_ids,
        project_ids: project_ids,
      },
    };
    try {
      setIsLoading(true);
      let response = await TLinkApi.post(
        `/reports/generic-list?search=${search}`,
        payload
      );
      setDropdownOptions((prev) => ({
        ...prev,
        [paramKey]: response.data,
      }));
      setFilteredOptions((prev) => ({
        ...prev,
        [paramKey]: response.data, // Set initial filtered options to all options
      }));
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.result?.userMessage, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDropdownSupplierData = async (paramKey, search = "") => {
    let payload = {
      parameter_type: paramKey,
      filter: {
        survey_ids: survey_ids,
      },
    };
    try {
      setIsLoading(true);
      let response = await TLinkApi.post(
        `/reports/generic-list?search=${search}`,
        payload
      );
      setDropdownOptions((prev) => ({
        ...prev,
        [paramKey]: response.data,
      }));
      setFilteredOptions((prev) => ({
        ...prev,
        [paramKey]: response.data, // Set initial filtered options to all options
      }));
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.result?.userMessage, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getReportParameters = async (reportId) => {
    try {
      setIsLoading(true);
      const response = await TLinkApi.get(`/reports/${reportId}/parameters`);
      const sortedParams = response.data.sort(
        (a, b) => a.sort_order - b.sort_order
      );
      setParameters(sortedParams);
      setSelectedReportId(reportId);
      sortedParams.forEach((param) => {
        if (["Single Select", "Multi Select"].includes(param.param_type_name)) {
          if (!["start_date", "end_date"].includes(param.param_key)) {
            fetchDropdownData(param.param_key);
          }
        }
      });
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.result?.userMessage, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDropdownData = async (paramKey, search = "") => {
    const filterKeys = [
      "client_ids",
      "project_ids",
      "tenant_ids",
      "survey_ids",
      "supplier_ids",
      "status_ids",
      "sub_status_ids",
      "qualification_category_type_ids",
      "reconciliation_data_type_ids",
      "reconciliation_type_ids",
    ];

    const filter = filterKeys.includes(paramKey) ? { [paramKey]: [] } : {};

    const payload = {
      parameter_type: paramKey,
      filter: {
        ...filter,
      },
    };

    try {
      setIsLoading(true);
      const response = await TLinkApi.post(
        `/reports/generic-list?search=${search}`,
        payload
      );
      setDropdownOptions((prev) => ({
        ...prev,
        [paramKey]: response.data,
      }));
      setFilteredOptions((prev) => ({
        ...prev,
        [paramKey]: _.uniqBy(
          [...response.data, ...(selectedOptions[paramKey] || [])],
          "id"
        ),
      }));
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.result?.userMessage, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (parameters?.some((param) => param.param_key === "tenant_ids")) {
      setSelectedOptions((prev) => ({
        ...prev,
        ["client_ids"]: [],
      }));
      setValue("client_ids", []);
      if (report !== -1 && tenant_ids && tenant_ids.length > 0) {
        fetchDropdownClientData("client_ids");
      } else if (report !== -1) {
        fetchDropdownClientData("client_ids");
      }
    }
  }, [tenant_ids, report]);

  useEffect(() => {
    if (parameters?.some((param) => param.param_key === "project_ids")) {
      setSelectedOptions((prev) => ({
        ...prev,
        ["project_ids"]: [],
      }));
      setValue("project_ids", []);
      if (report !== -1 && client_ids && client_ids.length > 0) {
        fetchDropdownProjectData("project_ids");
      } else if (report !== -1) {
        fetchDropdownProjectData("project_ids");
      }
    }
  }, [client_ids, report]);

  useEffect(() => {
    if (parameters?.some((param) => param.param_key === "survey_ids")) {
      setSelectedOptions((prev) => ({
        ...prev,
        ["survey_ids"]: [],
      }));
      setValue("survey_ids", []);
      if (report !== -1 && project_ids && project_ids.length > 0) {
        fetchDropdownSurveyData("survey_ids");
      } else if (report !== -1) {
        fetchDropdownSurveyData("survey_ids");
      }
    }
  }, [project_ids, report]);

  useEffect(() => {
    if (parameters?.some((param) => param.param_key === "supplier_ids")) {
      setSelectedOptions((prev) => ({
        ...prev,
        ["supplier_ids"]: [],
      }));
      setValue("supplier_ids", []);
      if (report !== -1 && survey_ids && survey_ids.length > 0) {
        fetchDropdownSupplierData("supplier_ids");
      } else if (report !== -1) {
        fetchDropdownSupplierData("supplier_ids");
      }
    }
  }, [survey_ids, report]);

  useEffect(() => {
    getReportList();
  }, []);

  const handleSearchChange = (paramKey, event) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery((prev) => ({
      ...prev,
      [paramKey]: query,
    }));
    setDebouncedSearches((prev) => ({
      ...prev,
      [paramKey]: query,
    }));
  };

  useEffect(() => {
    const delaySearchTimeouts = {};

    Object.entries(debouncedSearches).forEach(([paramKey, query]) => {
      delaySearchTimeouts[paramKey] = setTimeout(() => {
        fetchDropdownData(paramKey, query);
      }, 1500);
    });

    return () => {
      Object.values(delaySearchTimeouts).forEach(clearTimeout);
    };
  }, [debouncedSearches]);

  const save = async (formData) => {
    const filters = parameters.map((param) => ({
      name: param.param_key,
      value: formData[param.param_key] || "",
      sort_order: param.sort_order,
      fn_param_order: param.fn_param_order,
    }));

    const payload = {
      filters,
    };

    try {
      setIsLoading(true);
      const response = await TLinkApi.post(
        `/reports/${selectedReportId}/data`,
        payload
      );
      enqueueSnackbar(response?.message, {
        variant: "success",
      });
      reset();
      setValue("report", -1);
      setIsReportSelected(false);
      setSelectedReportId(null);
      setParameters([]);
      setDropdownOptions({});
      navigate("/reportHistory");
    } catch (error) {
      enqueueSnackbar(error?.response?.data?.result?.userMessage, {
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const validateDateRange = (start, end) => {
    if (!start || !end) return true;

    // Convert start and end to Date objects
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);

    // Ensure both dates are valid
    if (isNaN(startDateObj.getTime()) || isNaN(endDateObj.getTime())) {
      return "Invalid date format";
    }
    if (startDateObj > endDateObj) {
      return "The start date cannot be after the end date!";
    }

    // Calculate the difference in milliseconds
    const diffInMillis = endDateObj.getTime() - startDateObj.getTime();

    // Convert milliseconds to days
    const diffInDays = Math.ceil(diffInMillis / (1000 * 60 * 60 * 24));

    // Check if the date difference is greater than 60
    return diffInDays <= 60 || "The date range cannot exceed 60 days!";
  };

  return (
    <Box>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.modal + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Grid
        p={2}
        sx={{
          background: "#FFF",
          borderRadius: "10px",
        }}
      >
        {/* <CommonModal
                    heading={"Generic Reports"}
                    rightContent={
                        <StyledPlusTenant
                            onClick={() => navigate("/admin/reports")}
                            className="common-save-btn"
                        >
                            Report History
                        </StyledPlusTenant>
                    }
                    mainContent={ */}
        <Box
          sx={{
            margin: "0px",
            padding: "0px",
            height: "82vh",
            overflow: "auto",
          }}
        >
          <form onSubmit={handleSubmit(save)} noValidate>
            <Grid container spacing={3} sx={{}}>
              <Grid item xs={12} md={4}>
                <Controller
                  name="report"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.report} size="small">
                      <Typography variant="body2" color="black" gutterBottom>
                        Select Report *
                      </Typography>
                      <Select
                        {...field}
                        // label="Select Report *"
                        onChange={(event) => {
                          field.onChange(event);
                          getReportParameters(Number(event.target.value));
                          setIsReportSelected(true); // Enable the Send Report button when a report is selected
                        }}
                      >
                        {reportList && reportList?.length > 0 ? (
                          reportList?.map((report) => (
                            <MenuItem key={report.id} value={report.id}>
                              {report.name}
                            </MenuItem>
                          ))
                        ) : (
                          <MenuItem disabled>No reports available</MenuItem>
                        )}
                      </Select>
                      <FormHelperText>{errors.report?.message}</FormHelperText>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
            <Grid container spacing={3} sx={{ paddingTop: "25px" }}>
              {parameters && parameters.length > 0 ? (
                parameters.map((param) => (
                  <Grid item xs={12} md={4} key={param.id}>
                    <Controller
                      name={param.param_key}
                      control={control}
                      rules={{
                        required: param.is_required
                          ? `${param.display_name} is required!`
                          : false,
                        validate: (value) => {
                          // Validate the start_date and end_date fields
                          if (param.param_key === "start_date" && endDate) {
                            return (
                              validateDateRange(value, endDate) ||
                              "The date range cannot exceed 60 days!"
                            );
                          }
                          if (param.param_key === "end_date" && startDate) {
                            return (
                              validateDateRange(startDate, value) ||
                              "The date range cannot exceed 60 days!"
                            );
                          }
                          return true;
                        },
                      }}
                      render={({ field }) => {
                        if (
                          param.param_key === "start_date" ||
                          param.param_key === "end_date"
                        ) {
                          return (
                            <>
                              <Typography
                                variant="body2"
                                color="black"
                                gutterBottom
                              >
                                {param.display_name}{param.is_required?"*":""}
                              </Typography>
                              <TextField
                                {...field}
                                // sx={selectStyle}
                                type="date"
                                fullWidth
                                size="small"
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                error={!!errors[param.param_key]}
                                helperText={String(
                                  errors[param.param_key]?.message || ""
                                )}
                              />
                            </>
                          );
                        }
                        switch (param.param_type_key) {
                          case "Single Select":
                            return (
                              <FormControl
                                fullWidth
                                size="small"
                                error={!!errors[param.param_key]}
                                // sx={selectStyle}
                              >
                                <Typography
                                  variant="body2"
                                  color="black"
                                  gutterBottom
                                >
                                  {param.display_name}{param.is_required?"*":""}
                                </Typography>
                                <Select
                                  {...field}
                                  value={field.value || ""}
                                  onChange={(event) => {
                                    field.onChange(event.target.value);

                                    setSelectedOptions((prev) => ({
                                      ...prev,
                                      [param.param_key]: filteredOptions[
                                        param.param_key
                                      ].filter((vl) =>
                                        event.target.value.includes(vl.id)
                                      ),
                                    }));
                                  }}
                                  error={!!errors.report}
                                  helperText={errors.report?.message}
                                  endAdornment={
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={(_e) => {
                                          field.onChange([]);
                                          setSearchQuery((prev) => ({
                                            ...prev,
                                            [param.param_key]: "",
                                          }));
                                          setSelectedOptions((prev) => ({
                                            ...prev,
                                            [param.param_key]: [],
                                          }));
                                          // //(param.param_key, "param.param_key")
                                          if (
                                            param.param_key === "project_ids"
                                          ) {
                                            if (
                                              client_ids &&
                                              client_ids.length > 0
                                            ) {
                                              fetchDropdownProjectData(
                                                param.param_key
                                              );
                                            } else {
                                              fetchDropdownData(
                                                param.param_key
                                              );
                                            }
                                          } else if (
                                            param.param_key === "survey_ids"
                                          ) {
                                            if (
                                              project_ids &&
                                              project_ids.length > 0 &&
                                              client_ids &&
                                              client_ids.length > 0
                                            ) {
                                              fetchDropdownSurveyData(
                                                param.param_key
                                              );
                                            } else {
                                              fetchDropdownData(
                                                param.param_key
                                              );
                                            }
                                          } else if (
                                            param.param_key === "supplier_ids"
                                          ) {
                                            if (
                                              survey_ids &&
                                              survey_ids.length > 0
                                            ) {
                                              fetchDropdownSupplierData(
                                                param.param_key
                                              );
                                            } else {
                                              fetchDropdownData(
                                                param.param_key
                                              );
                                            }
                                          } else {
                                            fetchDropdownData(param.param_key);
                                          }
                                        }}
                                      >
                                        <ClearIcon />
                                      </IconButton>
                                    </InputAdornment>
                                  }
                                  required={param.is_required}
                                  fullWidth
                                >
                                  <ListSubheader sx={{ marginBottom: "1rem" }}>
                                    <TextField
                                      sx={{
                                        position: "sticky",
                                        marginTop: "-16px",
                                        paddingTop: "1rem",
                                        zIndex: 1,
                                        background: "white",
                                      }}
                                      placeholder="Type to search..."
                                      size="small"
                                      autoFocus
                                      fullWidth
                                      value={searchQuery[param.param_key] || ""}
                                      onChange={(e) =>
                                        handleSearchChange(param.param_key, e)
                                      }
                                      onKeyDown={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </ListSubheader>
                                  {filteredOptions[param.param_key] &&
                                  filteredOptions[param.param_key]?.length >
                                    0 ? (
                                    filteredOptions[param.param_key]?.map(
                                      (items) => (
                                        <MenuItem
                                          key={items.id}
                                          value={items.id}
                                        >
                                          {items.name}
                                        </MenuItem>
                                      )
                                    )
                                  ) : (
                                    <MenuItem disabled>
                                      No Option available
                                    </MenuItem>
                                  )}
                                </Select>
                                {/* <MuiSelect
                                  label={param.display_name}
                                  {...field}
                                  multiple={
                                    param.param_type_key === "Multi Select"
                                  }
                                  value={
                                    param.param_type_key === "Multi Select"
                                      ? field.value || []
                                      : field.value || ""
                                  }
                                  endAdornment={
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={(_e) => {
                                          field.onChange([]);
                                          setSearchQuery((prev) => ({
                                            ...prev,
                                            [param.param_key]: "",
                                          }));
                                          setSelectedOptions((prev) => ({
                                            ...prev,
                                            [param.param_key]: [],
                                          }));
                                          // //(param.param_key, "param.param_key")
                                          if (
                                            param.param_key === "project_ids"
                                          ) {
                                            if (
                                              client_ids &&
                                              client_ids.length > 0
                                            ) {
                                              fetchDropdownProjectData(
                                                param.param_key
                                              );
                                            } else {
                                              fetchDropdownData(
                                                param.param_key
                                              );
                                            }
                                          } else if (
                                            param.param_key === "survey_ids"
                                          ) {
                                            if (
                                              project_ids &&
                                              project_ids.length > 0 &&
                                              client_ids &&
                                              client_ids.length > 0
                                            ) {
                                              fetchDropdownSurveyData(
                                                param.param_key
                                              );
                                            } else {
                                              fetchDropdownData(
                                                param.param_key
                                              );
                                            }
                                          } else if (
                                            param.param_key === "supplier_ids"
                                          ) {
                                            if (
                                              survey_ids &&
                                              survey_ids.length > 0
                                            ) {
                                              fetchDropdownSupplierData(
                                                param.param_key
                                              );
                                            } else {
                                              fetchDropdownData(
                                                param.param_key
                                              );
                                            }
                                          } else {
                                            fetchDropdownData(param.param_key);
                                          }
                                        }}
                                      >
                                        <ClearIcon />
                                      </IconButton>
                                    </InputAdornment>
                                  }
                                  onChange={(event) => {
                                    field.onChange(event.target.value);

                                    setSelectedOptions((prev) => ({
                                      ...prev,
                                      [param.param_key]: filteredOptions[
                                        param.param_key
                                      ].filter((vl) =>
                                        event.target.value.includes(vl.id)
                                      ),
                                    }));
                                  }}
                                  onClose={() => {
                                    // Clear the search query when dropdown closes
                                    // setSearchQuery((prev) => ({
                                    //     ...prev,
                                    //     [param.param_key]: "",
                                    // }));
                                    // fetchDropdownData(param.param_key);
                                  }}
                                  options={filteredOptions[
                                    param.param_key
                                  ]?.map((reports) => ({
                                    value: reports.id,
                                    label: reports.name,
                                  }))}
                                /> */}
                                {/* <ListSubheader sx={{ marginBottom: "1rem" }}>
                                    <TextField
                                      sx={{
                                        position: "sticky",
                                        marginTop: "-16px",
                                        paddingTop: "1rem",
                                        zIndex: 1,
                                        background: "white",
                                      }}
                                      placeholder="Type to search..."
                                      size="small"
                                      autoFocus
                                      fullWidth
                                      value={searchQuery[param.param_key] || ""}
                                      onChange={(e) =>
                                        handleSearchChange(param.param_key, e)
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key !== "Escape") {
                                          e.stopPropagation();
                                        }
                                      }}
                                    />
                                  </ListSubheader>
                                  {filteredOptions[param.param_key]?.length >
                                  0 ? (
                                    filteredOptions[param.param_key].map(
                                      (option) => (
                                        <MenuItem
                                          key={option.id}
                                          value={option.id}
                                          onClick={(_e) => {
                                            // field.onChange([option.id])
                                            // setSelectedOptions((prev) => ({
                                            //     ...prev,
                                            //     [param.param_key]: [
                                            //         ...(prev[param.param_key] || []), // Ensure it's an array, even if initially undefined
                                            //         {
                                            //             id: option.id,
                                            //             name: option.name,
                                            //         },
                                            //     ],
                                            // }));
                                          }}
                                        >
                                          {option.name}
                                        </MenuItem>
                                      )
                                    )
                                  ) : (
                                    <MenuItem disabled>
                                      No options available
                                    </MenuItem>
                                  )}
                                </MuiSelect> */}
                                <FormHelperText>
                                  {String(
                                    errors[param.param_key]?.message || ""
                                  )}
                                </FormHelperText>
                              </FormControl>
                            );
                          case "Multi Select":
                            return (
                              <FormControl
                                fullWidth
                                size="small"
                                error={!!errors[param.param_key]}
                                // sx={selectStyle}
                              >
                                <Typography
                                  variant="body2"
                                  color="black"
                                  gutterBottom
                                >
                                  {param.display_name}{param.is_required?"*":""}
                                </Typography>
                                <Select
                                  {...field}
                                  multiple={
                                    param.param_type_key === "Multi Select"
                                  }
                                  value={
                                    param.param_type_key === "Multi Select"
                                      ? field.value || []
                                      : field.value || ""
                                  }
                                  endAdornment={
                                    <InputAdornment position="end">
                                      <IconButton
                                        onClick={(_e) => {
                                          field.onChange([]);
                                          setSearchQuery((prev) => ({
                                            ...prev,
                                            [param.param_key]: "",
                                          }));
                                          setSelectedOptions((prev) => ({
                                            ...prev,
                                            [param.param_key]: [],
                                          }));
                                          // //(param.param_key, "param.param_key")
                                          if (
                                            param.param_key === "project_ids"
                                          ) {
                                            if (
                                              client_ids &&
                                              client_ids.length > 0
                                            ) {
                                              fetchDropdownProjectData(
                                                param.param_key
                                              );
                                            } else {
                                              fetchDropdownData(
                                                param.param_key
                                              );
                                            }
                                          } else if (
                                            param.param_key === "survey_ids"
                                          ) {
                                            if (
                                              project_ids &&
                                              project_ids.length > 0 &&
                                              client_ids &&
                                              client_ids.length > 0
                                            ) {
                                              fetchDropdownSurveyData(
                                                param.param_key
                                              );
                                            } else {
                                              fetchDropdownData(
                                                param.param_key
                                              );
                                            }
                                          } else if (
                                            param.param_key === "supplier_ids"
                                          ) {
                                            if (
                                              survey_ids &&
                                              survey_ids.length > 0
                                            ) {
                                              fetchDropdownSupplierData(
                                                param.param_key
                                              );
                                            } else {
                                              fetchDropdownData(
                                                param.param_key
                                              );
                                            }
                                          } else {
                                            fetchDropdownData(param.param_key);
                                          }
                                        }}
                                      >
                                        <ClearIcon />
                                      </IconButton>
                                    </InputAdornment>
                                  }
                                  onChange={(event) => {
                                    field.onChange(event.target.value);

                                    setSelectedOptions((prev) => ({
                                      ...prev,
                                      [param.param_key]: filteredOptions[
                                        param.param_key
                                      ].filter((vl) =>
                                        event.target.value.includes(vl.id)
                                      ),
                                    }));
                                  }}
                                  onClose={() => {
                                    setSearchQuery((prev) => ({
                                      ...prev,
                                      [param.param_key]: "",
                                    }));
                                    fetchDropdownData(param.param_key);
                                  }}
                                >
                                  <ListSubheader sx={{ marginBottom: "1rem" }}>
                                    <TextField
                                      sx={{
                                        position: "sticky",
                                        marginTop: "-16px",
                                        paddingTop: "1rem",
                                        zIndex: 1,
                                        background: "white",
                                      }}
                                      placeholder="Type to search..."
                                      size="small"
                                      autoFocus
                                      fullWidth
                                      value={searchQuery[param.param_key] || ""}
                                      onChange={(e) =>
                                        handleSearchChange(param.param_key, e)
                                      }
                                      onKeyDown={(e) => e.stopPropagation()}
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                  </ListSubheader>
                                  {filteredOptions[param.param_key] &&
                                  filteredOptions[param.param_key]?.length >
                                    0 ? (
                                    filteredOptions[param.param_key]?.map(
                                      (items) => (
                                        <MenuItem
                                          key={items.id}
                                          value={items.id}
                                        >
                                          {items.name}
                                        </MenuItem>
                                      )
                                    )
                                  ) : (
                                    <MenuItem disabled>
                                      No Option available
                                    </MenuItem>
                                  )}
                                </Select>
                                {/* <ListSubheader sx={{ marginBottom: "1rem" }}>
                                    <TextField
                                      sx={{
                                        position: "sticky",
                                        marginTop: "-16px",
                                        paddingTop: "1rem",
                                        zIndex: 1,
                                        background: "white",
                                      }}
                                      placeholder="Type to search..."
                                      size="small"
                                      autoFocus
                                      fullWidth
                                      value={searchQuery[param.param_key] || ""}
                                      onChange={(e) =>
                                        handleSearchChange(param.param_key, e)
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key !== "Escape") {
                                          e.stopPropagation();
                                        }
                                      }}
                                    />
                                  </ListSubheader>
                                  {filteredOptions[param.param_key]?.length >
                                  0 ? (
                                    filteredOptions[param.param_key].map(
                                      (option) => (
                                        <MenuItem
                                          key={option.id}
                                          value={option.id}
                                          onClick={(_e) => {
                                            // field.onChange([option.id])
                                            // setSelectedOptions((prev) => ({
                                            //     ...prev,
                                            //     [param.param_key]: [
                                            //         ...(prev[param.param_key] || []), // Ensure it's an array, even if initially undefined
                                            //         {
                                            //             id: option.id,
                                            //             name: option.name,
                                            //         },
                                            //     ],
                                            // }));
                                          }}
                                        >
                                          {option.name}
                                        </MenuItem>
                                      )
                                    )
                                  ) : (
                                    <MenuItem disabled>
                                      No options available
                                    </MenuItem>
                                  )}
                                </MuiSelect> */}
                                <FormHelperText>
                                  {String(
                                    errors[param.param_key]?.message || ""
                                  )}
                                </FormHelperText>
                              </FormControl>
                            );
                          default:
                            return (
                              <TextField
                                {...field}
                                fullWidth
                                size="small"
                                error={!!errors[param.param_key]}
                                helperText={String(
                                  errors[param.param_key]?.message || ""
                                )}
                              />
                            );
                        }
                      }}
                    />
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    No reports selected.
                  </Typography>
                </Grid>
              )}
            </Grid>
            <Box
              mt={2}
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              gap="10px"
            >
              {isReportSelected && (
                <MDButton
                  variant="gradient"
                  color="info"
                  disabled={
                    isLoading ||
                    !isReportSelected ||
                    parameters.some(
                      (param) =>
                        param.is_required &&
                        (Array.isArray(watch(param.param_key))
                          ? watch(param.param_key)?.length === 0
                          : !watch(param.param_key))
                    )
                  }
                  sx={{
                    color:
                      isLoading ||
                      !isReportSelected ||
                      parameters.some(
                        (param) =>
                          param.is_required &&
                          (Array.isArray(watch(param.param_key))
                            ? watch(param.param_key)?.length === 0
                            : !watch(param.param_key))
                      )
                        ? "grey !important"
                        : "inherit",
                  }}
                  type="submit"
                >
                  <img
                    src={Union2}
                    width="15px"
                    height="15px"
                    style={{ paddingRight: "5px" }}
                    alt="export"
                  />
                  <span style={{ color: "#fff" }}>Export Report</span>
                </MDButton>
              )}
            </Box>
          </form>
        </Box>
        {/* } */}
        {/* /> */}
      </Grid>
    </Box>
  );
};

export default GenericReports;
